/**
 * S5 — Feature Gate Chip (inline beside every gated feature's entry button).
 *
 * Variant property: State = Trial | Locked | Hidden
 *   Trial  → shows "Trial · Xd left", click opens TrialStatusDetail (S3)
 *   Locked → shows "Premium" lock icon, click opens UpgradePromptModal (S4)
 *   Hidden → not rendered (feature is permanently paid)
 *
 * Figma: S5 — Locked Feature Entry-Point Chip
 */
import React from "react";

export type ChipState = "trial" | "locked" | "expired" | "active" | "hidden";

interface FeatureGateChipProps {
  state: ChipState;
  daysLeft?: number | null;
  onClick?: () => void;
}

export const FeatureGateChip: React.FC<FeatureGateChipProps> = ({ state, daysLeft, onClick }) => {
  if (state === "hidden" || state === "active") return null;

  const chipClass =
    state === "trial"
      ? daysLeft !== null && daysLeft !== undefined && daysLeft <= 2
        ? "bg-red-50 text-red-700 border-red-200"
        : "bg-blue-50 text-blue-700 border-blue-200"
      : state === "expired"
        ? "bg-red-50 text-red-700 border-red-200"
        : "bg-amber-50 text-amber-700 border-amber-200"; // locked

  const label =
    state === "trial" && daysLeft !== null && daysLeft !== undefined
      ? `Trial · ${daysLeft}d left`
      : state === "expired"
        ? "Expired"
        : "Premium";

  const icon =
    state === "trial" || state === "expired"
      ? <HourglassIcon />
      : <LockIcon />;

  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      {...(Tag === "button" ? { type: "button" as const } : {})}
      onClick={onClick}
      className={`inline-flex items-center gap-0.5 sm:gap-1 rounded-full border px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[11px] font-semibold transition-colors hover:opacity-80 cursor-pointer ${chipClass}`}
    >
      {icon}
      <span>{label}</span>
    </Tag>
  );
};

const HourglassIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M17 2v4.172a2 2 0 0 1-.586 1.414L12 12l-4.414-4.414A2 2 0 0 1 7 6.172V2" />
  </svg>
);

const LockIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

export default FeatureGateChip;
